import React, { ComponentProps } from 'react'
import { Spin } from 'antd'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useCalculationCountValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { getColorByIndex } from '../../util/colors'
import { getFontSize } from '../../util/fontSize'
import { BORDER_WIDTH } from '../../util/constants'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TITLE = 'Antal beräkningar per kommun'

export const CalculationCount: React.FC<{ text: React.ReactNode }> = ({ text }) => {
  const { data, isLoading, error } = useCalculationCountValues()

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} title={TITLE} />
  }

  if (error || !data) {
    return (
      <BenchmarkingContentRow
        title={TITLE}
        text={text}
        graph={
          <div className="flex h-full w-full items-center justify-center font-mono text-sm text-red-600">
            <span>{error ? `Fel vid hämtning av data: ${String(error)}` : 'Ingen data tillgänglig'}</span>
          </div>
        }
      />
    )
  }

  // En stapel per kommun — en serie med en färg per stapel
  const labels = data.map((r) => r.municipality_name as string)
  const counts = data.map((r) => Number(r.evaluation_count))

  const datasets = [
    {
      label: 'Antal beräkningar',
      data: counts,
      backgroundColor: data.map((_, i) => getColorByIndex(i).fill),
      borderColor: data.map((_, i) => getColorByIndex(i).border),
      borderWidth: BORDER_WIDTH,
    },
  ]

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.y} beräkningar`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: getFontSize('md') } },
      },
      y: {
        min: 0,
        ticks: { font: { size: getFontSize('md') } },
        title: { display: true, text: 'Antal beräkningar', font: { size: getFontSize('md') } },
      },
    },
  }

  const legend = (
    <BenchmarkingLegend
      items={data.map((r, i) => {
        const color = getColorByIndex(i)
        return { text: r.municipality_name as string, fillColor: color.fill, borderColor: color.border }
      })}
    />
  )

  return <BenchmarkingContentRow title={TITLE} text={text} legend={legend} graph={<Bar data={{ labels, datasets }} options={options} />} />
}
